import { FormInput, Modal, PrimaryButton, toast } from '@systemeio/ui';
import { useState } from 'react';
import TransLoco from 'shared/components/trans-loco';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useModal } from 'shared/hooks/use-modal';
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules';
import useUser from 'shared/hooks/use-user';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { ItemIntegration } from '../components/item-integration';
import { WrapperIntegration } from '../components/wrapper-integration';
import { WrapperIntegrationSkeleton } from '../components/wrapper-integtation-skeleton';
import { getHelpPageLinkTwilio } from '../utils/get-help-page-link-twilio';
import { useTwilioActions } from './hooks/use-twilio-actions';
import { useTwilioIntegration } from './hooks/use-twilio-integration';
import { useTwilioPhoneNumbers } from './hooks/use-twilio-phone-numbers';
import { TwilioIcon } from '@systemeio/ui/icons';
const initialErrors = {
  sid: '',
  authToken: ''
};
export function TwilioIntegration() {
  const {
    user
  } = useUser();
  const {
    t
  } = useLocoTranslation();
  const {
    revalidateModules
  } = useRevalidateModules();
  const {
    data,
    mutate
  } = useTwilioIntegration();
  const {
    postIntegration,
    deleteIntegration
  } = useTwilioActions();
  const {
    data: phoneNumbers,
    isValidating: isValidatingPhoneNumbers
  } = useTwilioPhoneNumbers();
  const integrationModal = useModal();
  const [sid, setSid] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState(initialErrors);
  const connectTwilioIntegration = async () => {
    if (isLoading) return;
    if (!sid || !authToken) {
      setErrors({
        sid: sid ? initialErrors.sid : t('dashboard.validation.empty_error.title'),
        authToken: authToken ? initialErrors.authToken : t('dashboard.validation.empty_error.title')
      });
      return;
    }
    setLoading(true);
    try {
      setErrors(initialErrors);
      const integration = await postIntegration({
        accountSid: sid,
        authToken: authToken
      });
      await mutate(integration, false);
      await revalidateModules([RequestModuleEnum.TwilioPhoneNumbers, RequestModuleEnum.TwilioMessageServices]);
      toast.success(t('dashboard.user_settings.integrations_page.twilio_integration.successfully_connected'));
    } catch (error) {} finally {
      setLoading(false);
    }
  };
  const disconnectTwilioIntegration = async () => {
    try {
      await deleteIntegration();
      await mutate(null, false);
      await revalidateModules([RequestModuleEnum.TwilioPhoneNumbers, RequestModuleEnum.TwilioMessageServices]);
      toast.success(t('dashboard.user_settings.integrations_page.twilio_integration.successfully_disconnected'));
    } catch (error_0) {}
  };
  const renderPhoneNumbers = () => {
    if (isValidatingPhoneNumbers) return <div className={'animate-pulse'}>
          <div className={'bg-skeleton-primary/40 h-[23px] rounded-lg px-7 text-transparent'}>
            .
          </div>
        </div>;
    if (!phoneNumbers) return '-';
    return phoneNumbers.join(', ');
  };
  if (data === undefined) return <WrapperIntegrationSkeleton />;
  return <>
      <WrapperIntegration icon={<TwilioIcon />} title={t('dashboard.user_settings.integrations_page.twilio_integration.name')} subTitle={t('dashboard.user_settings.integrations_page.twilio_integration.subtitle')} onClick={() => {
      integrationModal.open();
    }} isConnect={!!data && !data?.deactivated} isDeactivated={data?.deactivated} />

      <Modal title={t('dashboard.user_settings.integrations_page.twilio_integration_title')} opened={integrationModal.opened} onClose={() => integrationModal.close()} afterLeave={() => {
      setSid('');
      setAuthToken('');
      setErrors(initialErrors);
      setLoading(false);
    }} isFetching={isLoading}>
        {!data && <form onSubmit={e => {
        e.preventDefault();
        connectTwilioIntegration();
      }} className="flex flex-col gap-3.5">
            <div className={'mb-3.5'}>
              <span>
                <TransLoco t={t} i18nKey={'dashboard.user_settings.integrations_page.twilio.help'} components={{
              a: <a href={getHelpPageLinkTwilio(user?.dashboardLocale)} target="_blank" rel="noreferrer" className={'primary-link'} />,
              b: <b />
            }} />
              </span>
            </div>

            <FormInput required label={t('dashboard.user_settings.integrations_page.twilio_integration.input_sid_label')} placeholder={t('dashboard.user_settings.integrations_page.twilio_integration.input_sid_placeholder')} value={sid} onChange={e_0 => setSid(e_0.target.value)} disabled={isLoading} error={errors.sid} />

            <FormInput required label={t('dashboard.user_settings.integrations_page.twilio_integration.input_auth_token_label')} placeholder={t('dashboard.user_settings.integrations_page.twilio_integration.input_auth_token_placeholder')} value={authToken} onChange={e_1 => setAuthToken(e_1.target.value)} disabled={isLoading} error={errors.authToken} />

            <div className={'flex justify-center'}>
              <PrimaryButton type="submit" className="mt-2.5 lg:mt-7" isFetching={isLoading}>
                {t('global.save')}
              </PrimaryButton>
            </div>
          </form>}

        {data && <ItemIntegration removeIntegration={() => disconnectTwilioIntegration()} onLoading={setLoading} customParams={[{
        title: t('dashboard.user_settings.integrations_page.twilio_integration.integration_name'),
        value: data.accountName
      }, {
        title: t('global.created_on'),
        value: dateTimeFormat(data.createdAt, {
          timeFormat: user?.timeFormat,
          dateFormat: user?.dateFormat,
          timeZone: user?.timezone
        })
      }, {
        title: t('global.status'),
        value: data.deactivated ? t('global.deactivated') : t('global.active')
      }, {
        title: t('dashboard.user_settings.integrations_page.twilio_integration.sender_phone_numbers'),
        value: renderPhoneNumbers()
      }]} />}
      </Modal>
    </>;
}